<div class="req-page">
  <app-project-nav></app-project-nav>

  <app-page-header [title]="'requirements.pageTitle' | translate" [subtitle]="'requirements.pageSubtitle' | translate">
    <ng-container right>
      <app-requirements-stats-bar [tabCounts]="tabCounts()" />
      @if (!isProjectReader()) {
        <button class="req-create-btn" type="button" (click)="onCreate()">
          <mat-icon fontSet="material-icons-outlined" aria-hidden="true">add</mat-icon>
          {{ 'requirements.addButton' | translate }}
        </button>
      }
    </ng-container>
  </app-page-header>

  <app-requirements-filter-bar
    [searchTerm]="searchTerm()"
    [statusTab]="statusTab()"
    [typeFilter]="typeFilter()"
    [priorityFilter]="priorityFilter()"
    [authorFilter]="authorFilter()"
    [statusValueFilter]="statusValueFilter()"
    [tabCounts]="tabCounts()"
    [typeOptions]="typeFilterOptions()"
    [priorityOptions]="priorityFilterOptions()"
    [authorOptions]="authorFilterOptions()"
    [statusValueOptions]="statusValueFilterOptions()"
    [paginatorLength]="paginatorLength()"
    [savedViews]="savedViewsOptions()"
    [activeSavedView]="activeSavedViewName()"
    [projectId]="projectId"
    [currentLang]="currentLang()"
    (searchChange)="onSearchChange($event)"
    (tabChange)="setStatusTab($event)"
    (typeChange)="setFilter('type', $event)"
    (priorityChange)="setFilter('priority', $event)"
    (authorChange)="setFilter('author', $event)"
    (statusValueChange)="setFilter('statusValue', $event)"
    (savedViewSelected)="onSavedViewSelected($event)"
    (savedViewSaved)="onSavedViewSaved($event)"
    (savedViewDeleted)="onSavedViewDeleted($event)"
    (savedViewDeleteFailed)="onSavedViewDeleteFailed()"
  />

  @if (issuesError()) {
    <p class="req-hint req-hint--error" role="alert" data-testid="req-delete-error">
      {{ issuesError()! | translate }}
    </p>
  }

  @if (savedViewsError()) {
    <p class="req-hint req-hint--error" role="alert">{{ savedViewsError() }}</p>
  }

  @if (loading()) {
    <div class="req-loading"><mat-spinner diameter="32" /></div>
  } @else if (loadError()) {
    <p class="req-hint req-hint--error">{{ 'requirements.loadError' | translate }}</p>
  } @else if (sortedRows().length === 0) {
    <p class="req-hint">{{ 'requirements.empty' | translate }}</p>
  } @else {
    <div appListFocusScroll class="req-card">
      <div class="req-table-header">
        <div class="req-col req-col--id">
          <button type="button" class="req-sort-btn" (click)="sortBy('displayId')">
            ID
            <mat-icon
              class="req-sort-btn__icon"
              [class.req-sort-btn__icon--active]="tableSort().col === 'displayId'"
              aria-hidden="true"
            >
              {{
                tableSort().col === 'displayId'
                  ? tableSort().dir === 'asc'
                    ? 'arrow_upward'
                    : 'arrow_downward'
                  : 'unfold_more'
              }}
            </mat-icon>
          </button>
        </div>
        <div class="req-col req-col--title">
          <button type="button" class="req-sort-btn" (click)="sortBy('title')">
            {{ 'requirements.table.title' | translate }}
            <mat-icon
              class="req-sort-btn__icon"
              [class.req-sort-btn__icon--active]="tableSort().col === 'title'"
              aria-hidden="true"
            >
              {{
                tableSort().col === 'title'
                  ? tableSort().dir === 'asc'
                    ? 'arrow_upward'
                    : 'arrow_downward'
                  : 'unfold_more'
              }}
            </mat-icon>
          </button>
        </div>
        <div class="req-col req-col--type">
          <button type="button" class="req-sort-btn" (click)="sortBy('typeLabel')">
            {{ 'requirements.table.type' | translate }}
            <mat-icon
              class="req-sort-btn__icon"
              [class.req-sort-btn__icon--active]="tableSort().col === 'typeLabel'"
              aria-hidden="true"
            >
              {{
                tableSort().col === 'typeLabel'
                  ? tableSort().dir === 'asc'
                    ? 'arrow_upward'
                    : 'arrow_downward'
                  : 'unfold_more'
              }}
            </mat-icon>
          </button>
        </div>
        <div class="req-col req-col--priority">
          <button type="button" class="req-sort-btn" (click)="sortBy('priorityLabel')">
            {{ 'requirements.table.priority' | translate }}
            <mat-icon
              class="req-sort-btn__icon"
              [class.req-sort-btn__icon--active]="tableSort().col === 'priorityLabel'"
              aria-hidden="true"
            >
              {{
                tableSort().col === 'priorityLabel'
                  ? tableSort().dir === 'asc'
                    ? 'arrow_upward'
                    : 'arrow_downward'
                  : 'unfold_more'
              }}
            </mat-icon>
          </button>
        </div>
        <div class="req-col req-col--status">
          <button type="button" class="req-sort-btn" (click)="sortBy('statusLabel')">
            {{ 'requirements.table.status' | translate }}
            <mat-icon
              class="req-sort-btn__icon"
              [class.req-sort-btn__icon--active]="tableSort().col === 'statusLabel'"
              aria-hidden="true"
            >
              {{
                tableSort().col === 'statusLabel'
                  ? tableSort().dir === 'asc'
                    ? 'arrow_upward'
                    : 'arrow_downward'
                  : 'unfold_more'
              }}
            </mat-icon>
          </button>
        </div>
        <div class="req-col req-col--author">
          <button type="button" class="req-sort-btn" (click)="sortBy('author')">
            {{ 'requirements.table.author' | translate }}
            <mat-icon
              class="req-sort-btn__icon"
              [class.req-sort-btn__icon--active]="tableSort().col === 'author'"
              aria-hidden="true"
            >
              {{
                tableSort().col === 'author'
                  ? tableSort().dir === 'asc'
                    ? 'arrow_upward'
                    : 'arrow_downward'
                  : 'unfold_more'
              }}
            </mat-icon>
          </button>
        </div>
        <div class="req-col req-col--tags">{{ 'requirements.table.tags' | translate }}</div>
        <div class="req-col req-col--actions">{{ 'requirements.table.actions' | translate }}</div>
        <div class="req-row-arrow" aria-hidden="true"></div>
      </div>

      @for (row of pagedRows(); track row.id; let last = $last) {
        <div
          class="req-row"
          [class.req-row--last]="last"
          role="link"
          tabindex="0"
          [attr.aria-label]="row.title"
          (click)="onRowAction(row)"
          (keydown.enter)="onRowKeydown($event, row)"
        >
          <div class="req-col req-col--id">
            <app-code-id-chip [id]="row.displayId" />
          </div>
          <div class="req-col req-col--title">
            <span class="req-title">{{ row.title }}</span>
          </div>
          <div class="req-col req-col--type">
            <app-type-pill [kind]="row.typeKind" [label]="row.typeLabel | requirementTypeLabel" />
          </div>
          <div class="req-col req-col--priority">
            <app-priority-pill [level]="row.priorityLevel" [label]="row.priorityLabel" />
          </div>
          <div class="req-col req-col--status">
            <app-req-status-pill [kind]="row.statusKind" [label]="row.statusLabel | requirementStatusLabel" />
          </div>
          <div class="req-col req-col--author">
            @if (row.author) {
              <div class="req-author">
                <app-avatar [name]="row.author" [size]="28" />
                <span class="req-author__name">{{ row.author }}</span>
              </div>
            } @else {
              <span class="req-none">—</span>
            }
          </div>
          <div class="req-col req-col--tags">
            @if (row.tags.length === 0) {
              <span class="req-none">—</span>
            } @else {
              @for (tag of row.tags.slice(0, 2); track tag) {
                <span class="req-tag" [title]="tag">{{ tag }}</span>
              }
              @if (row.tags.length > 2) {
                <span class="req-tag-overflow">+{{ row.tags.length - 2 }}</span>
              }
            }
          </div>
          <div class="req-col req-col--actions">
            @if (!isProjectReader()) {
              <button
                type="button"
                class="req-action-btn req-action-btn--accent"
                [attr.aria-label]="'requirements.edit' | translate"
                (click)="$event.stopPropagation(); onRowAction(row)"
              >
                <mat-icon fontSet="material-icons-outlined" aria-hidden="true">edit</mat-icon>
              </button>
              <button
                type="button"
                class="req-action-btn req-action-btn--danger"
                [attr.aria-label]="'requirements.delete' | translate"
                (click)="$event.stopPropagation(); onDeleteRow(row)"
              >
                <mat-icon fontSet="material-icons-outlined" aria-hidden="true">delete</mat-icon>
              </button>
            }
          </div>
          <div class="req-row-arrow" aria-hidden="true">
            <mat-icon fontSet="material-icons-outlined">chevron_right</mat-icon>
          </div>
        </div>
      }
    </div>

    <div class="req-footer">
      @if (paginatorLength() > 0) {
        <span class="req-footer__range" role="status">
          {{
            'requirements.footer.rangeLabel'
              | translate: { start: rangeStart(), end: rangeEnd(), total: paginatorLength() }
          }}
        </span>
      }
      <div class="req-footer__pager">
        <label class="req-footer__label" for="req-page-size">
          {{ 'requirements.footer.rowsPerPage' | translate }}
        </label>
        <select
          id="req-page-size"
          class="req-footer__select"
          (change)="onPageSizeChange(+pageSizeSelect.value)"
          #pageSizeSelect
          [attr.aria-label]="'requirements.footer.rowsPerPageAriaLabel' | translate"
        >
          @for (size of pageSizeOptions; track size) {
            <option [value]="size" [selected]="pageSize() === size">{{ size }}</option>
          }
        </select>
        @if (pageCount() > 1) {
          <div class="req-footer__nav">
            <button
              type="button"
              class="req-action-btn"
              [disabled]="pageIndex() === 0"
              [attr.aria-label]="'requirements.footer.previousPage' | translate"
              (click)="onPageChange(pageIndex() - 1)"
            >
              <mat-icon aria-hidden="true">chevron_left</mat-icon>
            </button>
            <button
              type="button"
              class="req-action-btn"
              [disabled]="pageIndex() >= pageCount() - 1"
              [attr.aria-label]="'requirements.footer.nextPage' | translate"
              (click)="onPageChange(pageIndex() + 1)"
            >
              <mat-icon aria-hidden="true">chevron_right</mat-icon>
            </button>
          </div>
        }
      </div>
    </div>
  }
</div>

<app-confirm-dialog
  [visible]="showDeleteRequirement()"
  [title]="'requirements.deleteRequirement.title' | translate"
  [message]="deleteRequirementMessage()"
  [confirmLabel]="'requirements.deleteRequirement.confirm' | translate"
  [cancelLabel]="'requirements.deleteRequirement.cancel' | translate"
  [busy]="deleteRequirementBusy()"
  (confirmed)="onConfirmDeleteRequirement()"
  (cancelled)="onCancelDeleteRequirement()"
></app-confirm-dialog>
